import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Search as SearchIcon,
    TrendingUp,
    MapPin,
    CalendarDays,
    Briefcase,
    Users,
    Sparkles,
    ArrowRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getPublicEvents, getPublicServices, isEventPast } from "@/lib/entitiesStore";
import { getUsers } from "@/lib/storage";
import { useSocialVersion } from "@/lib/hooks";
import { ServiceCard } from "@/components/ServiceCard";
import { EventCard } from "@/components/EventCard";
import InitialAvatar from "@/components/InitialAvatar";
import { SERVICE_CATEGORIES } from "@/lib/serviceCategories";

/**
 * Discover screen — replaces the old "Alerts" tab.
 * Real discovery experience: search, categories, trending, near me,
 * events, services, people. Empty states only — no demo content.
 */
const Discover = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const v = useSocialVersion();
    void v;

    const services = useMemo(() => getPublicServices(), [v]);
    const events = useMemo(() => getPublicEvents().filter((e) => !isEventPast(e)), [v]);
    const people = useMemo(
        () => getUsers().filter((u) => u && u.id !== user?.id),
        [user, v]
    );

    const trending = useMemo(() => {
        const items = [
            ...services.slice(0, 6).map((s) => ({ kind: "service", ref: s })),
            ...events.slice(0, 6).map((e) => ({ kind: "event", ref: e })),
        ];
        return items.slice(0, 6);
    }, [services, events]);

    const myCity = (user?.location || "").split(",")[0]?.trim().toLowerCase();
    const nearMeItems = useMemo(() => {
        if (!myCity) return [];
        const inCity = (loc) => String(loc || "").toLowerCase().includes(myCity);
        return [
            ...services.filter((s) => inCity(s.location)).slice(0, 4).map((s) => ({ kind: "service", ref: s })),
            ...events.filter((e) => inCity(e.location)).slice(0, 4).map((e) => ({ kind: "event", ref: e })),
        ].slice(0, 6);
    }, [services, events, myCity]);

    return (
        <div className="min-h-full pb-8 animate-fade-in" data-testid="discover-screen">
            {/* Header + Search */}
            <header className="sticky top-0 z-10 px-5 pt-[max(14px,env(safe-area-inset-top))] pb-3 bg-background/90 backdrop-blur border-b border-border/60">
                <div className="flex items-center justify-between mb-3">
                    <h1 className="font-display text-[22px] font-semibold tracking-tight">
                        Discover
                    </h1>
                </div>
                <button
                    data-testid="discover-search-btn"
                    onClick={() => navigate("/search")}
                    className="w-full flex items-center gap-2.5 h-12 px-4 rounded-2xl bg-secondary border border-border text-left active:scale-[0.99] transition-transform"
                >
                    <SearchIcon size={16} className="text-muted-foreground" />
                    <span className="text-[13.5px] text-muted-foreground">
                        Search services, events, people…
                    </span>
                </button>
            </header>

            {/* Categories */}
            <Section
                title="Categories"
                icon={Sparkles}
                testId="discover-section-categories"
            >
                <div className="-mx-5 px-5 overflow-x-auto no-scrollbar" data-testid="discover-categories-row">
                    <div className="flex gap-2.5 min-w-max">
                        {SERVICE_CATEGORIES.slice(0, 12).map((c) => (
                            <button
                                key={c.id}
                                data-testid={`discover-category-${c.id}`}
                                onClick={() => navigate(`/search?cat=${encodeURIComponent(c.id)}`)}
                                className="shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-secondary text-foreground border border-border hover:bg-muted text-[12.5px] font-medium active:scale-[0.97] transition-transform"
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                </div>
            </Section>

            {/* Trending */}
            <Section
                title="Trending"
                icon={TrendingUp}
                testId="discover-section-trending"
            >
                {trending.length === 0 ? (
                    <Empty label="Nothing trending yet — check back soon." testId="discover-trending-empty" />
                ) : (
                    <HorizontalList testId="discover-trending-row">
                        {trending.map(({ kind, ref }) => (
                            <CardWrap key={`${kind}-${ref.id}`}>
                                {kind === "service"
                                    ? <ServiceCard service={ref} />
                                    : <EventCard event={ref} />}
                            </CardWrap>
                        ))}
                    </HorizontalList>
                )}
            </Section>

            {/* Near Me */}
            <Section
                title={myCity ? `Near you · ${capitalize(myCity)}` : "Near Me"}
                icon={MapPin}
                testId="discover-section-near-me"
            >
                {nearMeItems.length === 0 ? (
                    <Empty
                        label={
                            myCity
                                ? "Nothing near you yet — be the first to list something."
                                : "Add your location in Profile to see what's near you."
                        }
                        testId="discover-near-me-empty"
                    />
                ) : (
                    <HorizontalList testId="discover-near-me-row">
                        {nearMeItems.map(({ kind, ref }) => (
                            <CardWrap key={`${kind}-${ref.id}`}>
                                {kind === "service"
                                    ? <ServiceCard service={ref} />
                                    : <EventCard event={ref} />}
                            </CardWrap>
                        ))}
                    </HorizontalList>
                )}
            </Section>

            {/* Events */}
            <Section
                title="Events"
                icon={CalendarDays}
                testId="discover-section-events"
                onSeeAll={() => navigate("/discover/events")}
            >
                {events.length === 0 ? (
                    <Empty
                        label="No events yet — be the first to create one"
                        ctaLabel="Create event"
                        ctaTestId="discover-events-empty-cta"
                        onCta={() => navigate("/create/event")}
                        testId="discover-events-empty"
                    />
                ) : (
                    <div className="grid grid-cols-1 gap-3" data-testid="discover-events-grid">
                        {events.slice(0, 4).map((e) => <EventCard key={e.id} event={e} />)}
                    </div>
                )}
            </Section>

            {/* Services */}
            <Section
                title="Services"
                icon={Briefcase}
                testId="discover-section-services"
                onSeeAll={() => navigate("/discover/services")}
            >
                {services.length === 0 ? (
                    <Empty
                        label="No services available yet"
                        ctaLabel="Create service"
                        ctaTestId="discover-services-empty-cta"
                        onCta={() => navigate("/create/service")}
                        testId="discover-services-empty"
                    />
                ) : (
                    <div className="grid grid-cols-2 gap-3" data-testid="discover-services-grid">
                        {services.slice(0, 4).map((s) => <ServiceCard key={s.id} service={s} />)}
                    </div>
                )}
            </Section>

            {/* People */}
            <Section
                title="People"
                icon={Users}
                testId="discover-section-people"
            >
                {people.length === 0 ? (
                    <Empty label="No people to discover yet" testId="discover-people-empty" />
                ) : (
                    <HorizontalList testId="discover-people-row">
                        {people.slice(0, 8).map((p) => (
                            <button
                                key={p.id}
                                data-testid={`discover-person-${p.id}`}
                                onClick={() => navigate(`/u/${p.username}`)}
                                className="shrink-0 w-32 p-3 rounded-2xl border border-border bg-card hover:bg-accent/40 active:scale-[0.97] transition-[transform,background-color] flex flex-col items-center text-center"
                            >
                                {p.avatar ? (
                                    <img src={p.avatar} alt={p.fullName} className="h-14 w-14 rounded-full object-cover ring-2 ring-border" />
                                ) : (
                                    <InitialAvatar name={p.fullName || p.username} size={56} />
                                )}
                                <div className="mt-2 font-display text-[13px] font-semibold truncate w-full">
                                    {p.fullName || p.username}
                                </div>
                                <div className="text-[11px] text-muted-foreground truncate w-full">
                                    @{p.username}
                                </div>
                            </button>
                        ))}
                    </HorizontalList>
                )}
            </Section>
        </div>
    );
};

const Section = ({ title, icon: Icon, children, testId, onSeeAll }) => (
    <section className="px-5 pt-6" data-testid={testId}>
        <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-[15px] font-semibold tracking-tight inline-flex items-center gap-2">
                {Icon && <Icon size={15} className="text-primary" />}
                {title}
            </h2>
            {onSeeAll && (
                <button
                    data-testid={`${testId}-see-all`}
                    onClick={onSeeAll}
                    className="text-[12px] font-medium text-primary inline-flex items-center gap-0.5 hover:underline"
                >
                    See all <ArrowRight size={11} />
                </button>
            )}
        </div>
        {children}
    </section>
);

const HorizontalList = ({ children, testId }) => (
    <div className="-mx-5 px-5 overflow-x-auto no-scrollbar" data-testid={testId}>
        <div className="flex gap-3 min-w-max">{children}</div>
    </div>
);

const CardWrap = ({ children }) => (
    <div className="w-[260px] shrink-0">{children}</div>
);

const Empty = ({ label, ctaLabel, onCta, testId, ctaTestId }) => (
    <div
        className="rounded-2xl border border-dashed border-border bg-card/40 px-5 py-8 text-center"
        data-testid={testId}
    >
        <p className="text-[13px] text-muted-foreground">{label}</p>
        {ctaLabel && (
            <button
                data-testid={ctaTestId}
                onClick={onCta}
                className="mt-3 inline-flex items-center justify-center h-10 px-4 rounded-full text-primary-foreground font-medium text-[12.5px] active:scale-[0.98]"
                style={{
                    background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                    boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                }}
            >
                {ctaLabel}
            </button>
        )}
    </div>
);

const capitalize = (s) =>
    String(s || "").charAt(0).toUpperCase() + String(s || "").slice(1);

export default Discover;
